// chat-restore-helpers.js — pure decision logic for composing a history restore
// with a live streaming run (no DOM; unit-tested in chat-restore-helpers.test.mjs).
//
// A mid-run page reload starts two concurrent flows: the persisted-history fetch
// and the run socket's resume replay, which rebuilds the in-flight turn frame by
// frame. When the fetch resolves AFTER replay frames have already landed, a plain
// "replace the transcript" restore would clobber the half-rebuilt streaming turn —
// and the persisted history can never contain that turn (a turn persists only at
// final, which is also when its _streaming flag is cleared). These helpers decide
// what the restore must carry across the rebuild.

/** True for the one message shape that means "the agent is streaming this now". */
export function isLiveStreamingMsg(msg) {
  return !!(msg && msg.role === "assistant" && msg._streaming);
}

/**
 * The trailing in-flight turn to carry across a history restore, or null.
 * Only the LAST message can be live (the stream always folds into the tail),
 * and a live turn is by construction absent from persisted history — but if an
 * incoming message somehow carries the same id, the store already owns it and
 * carrying a copy would duplicate the turn, so decline.
 *
 * @param {Array} currentMessages  — the session's buffer at restore time
 * @param {Array} incomingMessages — the persisted history about to replace it
 * @returns {object|null} the live message object to re-append after the load
 */
export function liveTurnCarryover(currentMessages, incomingMessages) {
  const last = Array.isArray(currentMessages) && currentMessages.length
    ? currentMessages[currentMessages.length - 1]
    : null;
  if (!isLiveStreamingMsg(last)) return null;
  if (last.id && (incomingMessages || []).some((m) => m && m.id === last.id)) return null;
  return last;
}
